import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  format,
  parse,
  addMinutes,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  getDay,
  isValid,
  set,
} from 'date-fns';
import { Box, useColorModeValue } from '@chakra-ui/react';
import { es } from 'date-fns/locale';
import SchedulerModal from './SchedulerModal';
import authService from 'services/authService';
import { normalizeDurationMinutes } from '../../utils/serviceDuration';
import { toDateOnlyYYYYMMDD } from '../../utils/dateOnly';
import {
  formatSchedulerCalendarTitle,
  getCalendarCardLines,
} from '../../utils/calendarEventTitle';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { locale: es }),
  getDay: (date) => getDay(date),
  locales: { es },
});

const WORK_START_HOUR = 7;
const WORK_START_MINUTES = 30;
const WORK_END_HOUR = 16;
const CALENDAR_MIN = new Date(1970, 0, 1, WORK_START_HOUR, WORK_START_MINUTES, 0, 0);
const CALENDAR_MAX = new Date(1970, 0, 1, WORK_END_HOUR, 0, 0, 0);
const SCROLL_TO = new Date(1970, 0, 1, WORK_START_HOUR, WORK_START_MINUTES, 0, 0);

/** Rango de fechas visible para cada vista, con padding para las semanas de mes que asoman a otro mes. */
const getViewRange = (date, view) => {
  if (view === 'month') {
    return {
      start: startOfWeek(startOfMonth(date), { locale: es }),
      end: endOfWeek(endOfMonth(date), { locale: es }),
    };
  }
  if (view === 'work_week' || view === 'week') {
    return {
      start: startOfWeek(date, { locale: es }),
      end: endOfWeek(date, { locale: es }),
    };
  }
  return { start: date, end: date };
};

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const normalized =
    typeof dateStr === 'string'
      ? toDateOnlyYYYYMMDD(dateStr) || dateStr.slice(0, 10)
      : dateStr;
  const parsed = parse(normalized, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
};

const parseSlotDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const datePart =
    typeof dateStr === 'string'
      ? toDateOnlyYYYYMMDD(dateStr) || dateStr.slice(0, 10)
      : toDateOnlyYYYYMMDD(dateStr);
  if (!datePart) return null;
  const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const parsed = parse(
    `${datePart} ${normalizedTime}`,
    'yyyy-MM-dd HH:mm:ss',
    new Date(),
  );
  return isValid(parsed) ? parsed : null;
};

const CalendarEventCard = ({ event }) => {
  if (!event?.appointment) {
    return <span>{event?.title}</span>;
  }
  const lines = getCalendarCardLines(event.appointment, {
    start: event.start,
    end: event.end,
  });
  return (
    <div>
      {lines.map((line, index) => (
        <div key={`${index}-${line}`} className="scheduler-event-line">
          {line}
        </div>
      ))}
    </div>
  );
};

const SchedulerCalendar = ({
  appointmentList,
  holidayList,
  productsList,
  handleUpdateEvent,
  vehicleService,
  selectedEvent,
  setSelectedEvent,
  putVehicleService,
  putDynamicTest,
  putVehicleData,
  statusList,
  mechanicsList,
  vehicleIdsWithAlerts = [],
  onAlertsChanged,
  onAppointmentsRefresh,
  onAppointmentRangeChange,
  onVehicleServiceUpdated,
  onAppointmentLocalUpdate,
}) => {
  const isMechanic = authService.isMechanic();

  const [calendarDate, setCalendarDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState(isMechanic ? 'day' : 'work_week');

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const calendarViews = { month: true, work_week: true, day: true };
  const calendarView = calendarViews[currentView] ? currentView : 'day';

  const onRangeChangeRef = useRef(onAppointmentRangeChange);
  onRangeChangeRef.current = onAppointmentRangeChange;
  const lastRequestedRangeRef = useRef('');

  useEffect(() => {
    const notify = onRangeChangeRef.current;
    if (!notify) return;
    const { start, end } = getViewRange(calendarDate, calendarView);
    const from = format(start, 'yyyy-MM-dd');
    const to = format(end, 'yyyy-MM-dd');
    const rangeKey = `${from}|${to}`;
    if (lastRequestedRangeRef.current === rangeKey) return;
    lastRequestedRangeRef.current = rangeKey;
    notify(from, to);
  }, [calendarDate, calendarView]);

  const handleEventClick = (event) => {
    if (!event?.appointment) return;
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setIsModalOpen(false);
  };

  const generateOccupiedEvents = useCallback(
    (appointments) => {
      const occupiedEvents = appointments
        .filter(
          (appointment) =>
            String(appointment?.status || '').toLowerCase() !== 'cancelado',
        )
        .map((appointment) => {
          const start = parseSlotDateTime(appointment.date, appointment.time);
          if (!start) return null;
          const day = getDay(start);
          if (day === 0 || day === 6) return null;

          const durationMin = normalizeDurationMinutes(
            appointment.estimatedTime,
            appointment.service?.servicesEvents,
          );
          const end = addMinutes(start, durationMin);

          return {
            title: formatSchedulerCalendarTitle(appointment, { start, end }),
            start,
            end,
            selectable: true,
            appointment,
          };
        })
        .filter(Boolean);

      const holidayEvents = (holidayList || [])
        .map(({ date, name }) => {
          const day = parseLocalDate(date);
          if (!day) return null;
          const start = set(day, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
          const end = set(day, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 });
          return {
            title: name,
            status: 'holiday',
            start,
            end,
            selectable: false,
          };
        })
        .filter(Boolean);

      return [...occupiedEvents, ...holidayEvents];
    },
    [holidayList],
  );

  const allEvents = useMemo(
    () => generateOccupiedEvents(appointmentList),
    [appointmentList, generateOccupiedEvents],
  );

  const eventStyleGetter = (event) => {
    const hasAlert =
      event.appointment?.vehicleId &&
      vehicleIdsWithAlerts.includes(event.appointment.vehicleId);

    const baseEventStyle = {
      borderRadius: '4px',
      opacity: 0.95,
      fontSize: '11px',
      minHeight: '22px',
      pointerEvents: 'auto',
      cursor: 'pointer',
    };

    if (event.status === 'holiday' || event.status === 'weekend') {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#FED7D7',
          color: '#742A2A',
          border: '1px solid #FC8181',
          pointerEvents: 'none',
          cursor: 'default',
        },
      };
    }

    if (hasAlert) {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#FEFCBF',
          color: '#744210',
          border: '2px solid #D69E2E',
        },
      };
    }

    if (event.appointment?.status === 'Pendiente') {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#FFFAF0',
          color: '#744210',
          border: '1px solid #D69E2E',
        },
      };
    }
    if (event.appointment?.status === 'Confirmado') {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#EBF8FF',
          color: '#2A4365',
          border: '1px solid #4299E1',
        },
      };
    }
    if (event.appointment?.status === 'Recepcionado') {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#FAF5FF',
          color: '#44337A',
          border: '1px solid #9F7AEA',
        },
      };
    }
    if (event.appointment?.status === 'En curso') {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#FFFAF0',
          color: '#7B341E',
          border: '2px solid #ED8936',
        },
      };
    }
    if (
      event.appointment?.status === 'Completado' ||
      event.appointment?.status === 'Finalizado'
    ) {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#F0FFF4',
          color: '#22543D',
          border: '1px solid #48BB78',
        },
      };
    }
    if (event.appointment?.status === 'Cancelado') {
      return {
        style: {
          ...baseEventStyle,
          backgroundColor: '#FFF5F5',
          color: '#742A2A',
          border: '1px solid #FC8181',
        },
      };
    }

    return {
      style: {
        ...baseEventStyle,
        backgroundColor: '#EDF2F7',
        color: '#1A202C',
        border: '1px solid #A0AEC0',
      },
    };
  };

  const handleSlotSelect = ({ start }) => {
    setCalendarDate(start);
    setCurrentView('day');
  };

  const handleViewChange = (view) => {
    if (calendarViews[view]) {
      setCurrentView(view);
    }
  };

  const CustomToolbar = ({ label, date, onNavigate, onView }) => {
    const year = new Date(date).getFullYear();
    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group">
          <button type="button" onClick={() => onNavigate('TODAY')}>
            Hoy
          </button>
          <button type="button" onClick={() => onNavigate('PREV')}>
            Anterior
          </button>
          <button type="button" onClick={() => onNavigate('NEXT')}>
            Siguiente
          </button>
        </span>
        <span className="rbc-toolbar-label">{`${label.charAt(0).toUpperCase()}${label.slice(1)} / ${year}`}</span>
        <select
          value={calendarView}
          onChange={(e) => {
            const view = e.target.value;
            setCurrentView(view);
            onView(view);
          }}
          style={{
            padding: '6px 10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          <option value="month">Mes</option>
          <option value="work_week">Semana</option>
          <option value="day">Día</option>
        </select>
      </div>
    );
  };

  return (
    <Box
      flex="1"
      minH={0}
      h="100%"
      display="flex"
      flexDirection="column"
      position="relative"
      zIndex={1}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg="white"
      p={{ base: 2, md: 3 }}
      overflow="hidden"
      sx={{
        '.rbc-calendar': {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          fontSize: '12px',
        },
        '.rbc-toolbar': {
          mb: 2,
          flexWrap: 'wrap',
          gap: '8px',
          position: 'relative',
          zIndex: 2,
        },
        '.rbc-toolbar button': {
          fontSize: '12px',
          padding: '4px 10px',
          cursor: 'pointer',
        },
        '.rbc-time-view': {
          flex: 1,
          minHeight: 0,
          border: '1px solid',
          borderColor: 'gray.200',
          borderRadius: 'md',
        },
        '.rbc-time-content': {
          overflowY: 'auto',
          pointerEvents: 'auto',
        },
        '.rbc-time-view, .rbc-month-view, .rbc-day-slot, .rbc-time-slot': {
          pointerEvents: 'auto',
        },
        '.rbc-events-container': {
          pointerEvents: 'none',
        },
        '.rbc-event, .rbc-event-content': {
          pointerEvents: 'auto',
        },
        '.rbc-toolbar button, .rbc-toolbar select': {
          pointerEvents: 'auto',
        },
        '.rbc-month-view': {
          flex: 1,
          minHeight: 0,
          border: '1px solid',
          borderColor: 'gray.200',
          borderRadius: 'md',
        },
        '.rbc-event': {
          minHeight: '22px',
        },
        '.rbc-event-label': {
          display: 'none',
        },
        '.rbc-event-content': {
          whiteSpace: 'normal',
          lineHeight: '1.2',
        },
        '.scheduler-event-line': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        /* Hoy / día activo: gris muy claro para no confundir con Confirmado (azul) */
        '.rbc-today': {
          backgroundColor: '#F7FAFC !important',
        },
        '.rbc-day-bg.rbc-today': {
          backgroundColor: '#F7FAFC !important',
        },
        '.rbc-time-content .rbc-day-slot.rbc-today': {
          backgroundColor: 'transparent',
        },
        '.rbc-month-view .rbc-day-bg.rbc-today + .rbc-date-cell, .rbc-month-view .rbc-date-cell.rbc-now': {
          fontWeight: 'bold',
        },
        '.rbc-slot-selection': {
          backgroundColor: 'rgba(160, 174, 192, 0.2) !important',
        },
      }}
    >
      <Calendar
        localizer={localizer}
        culture="es"
        events={allEvents}
        startAccessor="start"
        endAccessor="end"
        selectable="ignoreEvents"
        view={calendarView}
        views={calendarViews}
        step={15}
        timeslots={2}
        min={CALENDAR_MIN}
        max={CALENDAR_MAX}
        scrollToTime={SCROLL_TO}
        date={calendarDate}
        onView={handleViewChange}
        onNavigate={setCalendarDate}
        onSelectSlot={handleSlotSelect}
        eventPropGetter={eventStyleGetter}
        components={{ toolbar: CustomToolbar, event: CalendarEventCard }}
        onSelectEvent={handleEventClick}
        popup
        dayLayoutAlgorithm="no-overlap"
        style={{ height: '100%', flex: 1, minHeight: 0 }}
      />
      <SchedulerModal
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
        handleUpdateEvent={handleUpdateEvent}
        vehicleService={vehicleService}
        putVehicleService={putVehicleService}
        putDynamicTest={putDynamicTest}
        productsList={productsList}
        putVehicleData={putVehicleData}
        statusList={statusList}
        mechanicsList={mechanicsList}
        appointmentList={appointmentList}
        holidayList={holidayList}
        onAlertsChanged={onAlertsChanged}
        onAppointmentsRefresh={onAppointmentsRefresh}
        onVehicleServiceUpdated={onVehicleServiceUpdated}
        onAppointmentLocalUpdate={onAppointmentLocalUpdate}
      />
    </Box>
  );
};

export default SchedulerCalendar;
